'use client'

import { useEffect, useState } from 'react'
import Image from 'next/image'
import Personas from '@/components/landing-v2/Personas'
import HowItWorks from '@/components/landing-v2/HowItWorks'
import FeaturesGrid from '@/components/landing-v2/FeatureCards'
import TestimonialsMarquee from '@/components/landing-v2/TestimonialsMarquee'
import Pricing from '@/components/landing-v2/Pricing'
import FAQ from '@/components/landing-v2/FAQ'
import ClientOnly from '@/components/landing-v2/ClientOnly'
import HeroViz from '@/components/landing-v2/HeroViz'
import Integrations from '@/components/landing-v2/Integrations'
import VideoModal from '@/components/landing/video-modal'
import { usePlatformLink } from '@/hooks/usePlatformLink'
import {
  trackCTAClick,
  trackTrialStart,
  trackVideoView,
  trackAppStoreClick,
  trackFooterLink,
  trackNavigation,
} from '@/lib/analytics'

const DEMO_VIDEO_URL = 'https://www.youtube.com/watch?v=MxlJLKiwn8w&list=PLNNwmjVjsnowCdqzgs2j3aFWLQiQzF0Ez'

const APP_STORE_URL = 'https://apple.co/3Cmgy10'
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.jegan.aiva&pcampaignid=web_share'
const CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/ava-ai-productivity-assis/dnfcodcajlmhgbobjicfblefnonhhfol?authuser=2&hl=en'

const heroVizPlaceholder = (
  <div style={{ width: '100%', aspectRatio: '1 / 1', maxWidth: 540, margin: '0 auto' }} />
)

function Icon({ name, size = 16, stroke = 2 }) {
  const c = {
    width: size, height: size, viewBox: '0 0 24 24', fill: 'none',
    stroke: 'currentColor', strokeWidth: stroke,
    strokeLinecap: 'round', strokeLinejoin: 'round',
  }
  switch (name) {
    case 'arrow':    return (<svg {...c}><path d="M5 12h14"/><path d="M13 6l6 6-6 6"/></svg>)
    case 'play':     return (<svg {...c}><path d="M7 5v14l12-7L7 5z" fill="currentColor"/></svg>)
    case 'mic':      return (<svg {...c}><rect x="9" y="3" width="6" height="12" rx="3"/><path d="M5 11a7 7 0 0 0 14 0"/><path d="M12 18v3"/></svg>)
    case 'calendar': return (<svg {...c}><rect x="3.5" y="5" width="17" height="15" rx="2"/><path d="M3.5 10h17"/><path d="M8 3v4"/><path d="M16 3v4"/></svg>)
    case 'note':     return (<svg {...c}><path d="M6 3h9l4 4v14H6z"/><path d="M14 3v5h5"/><path d="M9 13h7"/><path d="M9 17h5"/></svg>)
    case 'phone':    return (<svg {...c}><rect x="7" y="2" width="10" height="20" rx="2.5"/><path d="M11 18h2"/></svg>)
    case 'apple':    return (<svg {...c}><path d="M14.5 3.5c-.6.7-1.6 1.2-2.5 1.2 0-.9.5-1.9 1.1-2.5.6-.7 1.6-1.2 2.4-1.2.1.9-.4 1.8-1 2.5z"/><path d="M17.5 18c-.6 1-1.3 2-2.4 2-1.1 0-1.4-.6-2.6-.6s-1.6.6-2.6.6c-1.1 0-1.9-1-2.5-2-1.4-2-2.5-5.6-1-8 .8-1.2 2.1-2 3.5-2 1 0 1.9.6 2.6.6.7 0 1.7-.7 2.9-.6 1.4 0 2.7.6 3.5 1.7-2.4 1.4-2 4.7.6 5.7-.5 1.5-1 2.6-2 4.6z"/></svg>)
    case 'android':  return (<svg {...c}><path d="M5 17V11a7 7 0 0 1 14 0v6z"/><path d="M8 17v3"/><path d="M16 17v3"/><path d="M8 8 6 5"/><path d="M16 8l2-3"/></svg>)
    case 'chrome':   return (<svg {...c}><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3.2"/><path d="M12 9h8.5"/><path d="M9.4 13.5 5 20"/><path d="M14.6 13.5 19 20"/></svg>)
    default:         return null
  }
}

function Nav({ platformLink, isMobile }) {
  const [light, setLight] = useState(false)
  useEffect(() => {
    const onScroll = () => setLight(window.scrollY > 700)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  const target = isMobile ? '_blank' : undefined
  const rel = isMobile ? 'noopener noreferrer' : undefined
  return (
    <nav className={'nav' + (light ? ' is-light' : '')}>
      <div className="wrap nav-inner">
        <a href="/" className="logo" onClick={() => trackNavigation('logo')}>
          <span className="logo-mark" />
          <span>
            AVA
            <span className="logo-tag">Voice AI for work</span>
          </span>
        </a>
        <div className="nav-links">
          <a className="active" href="#features" onClick={() => trackNavigation('product')}>Product</a>
          <a href="#features" onClick={() => trackNavigation('features')}>Features</a>
          <a href="#how" onClick={() => trackNavigation('how_it_works')}>How it works</a>
          <a href="#pricing" onClick={() => trackNavigation('pricing')}>Pricing</a>
          <a href="#testimonials" onClick={() => trackNavigation('testimonials')}>Testimonials</a>
        </div>
        <div className="nav-right">
          <a href="/login" className="nav-signin" onClick={() => trackCTAClick('Sign in', 'nav')}>Sign in</a>
          <a
            href={platformLink}
            target={target}
            rel={rel}
            className="btn btn-pill"
            onClick={() => {
              trackCTAClick('Try AVA Free', 'nav')
              trackTrialStart('nav')
            }}
          >
            Try AVA Free
          </a>
        </div>
      </div>
    </nav>
  )
}

export default function V2Page() {
  const [videoOpen, setVideoOpen] = useState(false)
  const { platformLink, isMobile } = usePlatformLink()
  const target = isMobile ? '_blank' : undefined
  const rel = isMobile ? 'noopener noreferrer' : undefined

  const openDemo = () => {
    setVideoOpen(true)
    trackVideoView('30sec_demo')
  }

  return (
    <div className="ava-v2">
      <Nav platformLink={platformLink} isMobile={isMobile} />

      {/* Hero — dark slab */}
      <section className="hero atmos-dark">
        <div className="wrap atmos-content hero-grid">
          <div>
            <div className="pill">
              <span className="dot" />
              AI-Powered Productivity Assistant
            </div>
            <h1>Plan your day in 10 seconds — just talk to <em>AVA.</em></h1>
            <p className="hero-sub">
              Voice-first AI that replaces <em>10+ apps</em> &mdash; tasks, calendar,
              notes, meetings, and video summaries &mdash; in one place.
            </p>
            <div className="hero-ctas">
              <a
                href={platformLink}
                target={target}
                rel={rel}
                className="btn btn-primary btn-lg"
                onClick={() => {
                  trackCTAClick('Try AVA Free', 'hero')
                  trackTrialStart('hero')
                }}
              >
                Try AVA Free <Icon name="arrow" size={16} stroke={2} />
              </a>
              <button type="button" onClick={openDemo} className="btn btn-secondary btn-lg">
                <Icon name="play" size={14} stroke={2} /> Watch 30-sec demo
              </button>
            </div>
            <p className="hero-micro">No card for 14 days &middot; Keep your data private</p>
            <p className="hero-proof">
              50K<em>+</em> installs &middot; 4.7<em>&#9733;</em> average &middot;
              iOS &middot; Android &middot; Web &middot; Chrome
            </p>
            <div className="stores">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="store-pill"
                onClick={() => trackAppStoreClick('iOS', 'hero')}
              >
                <span className="store-pill-ico"><Image src="/icons/apple.svg" alt="" width={22} height={22} /></span>
                <span className="big">App Store</span>
              </a>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="store-pill"
                onClick={() => trackAppStoreClick('Android', 'hero')}
              >
                <span className="store-pill-ico"><Image src="/icons/android.svg" alt="" width={22} height={22} /></span>
                <span className="big">Google Play</span>
              </a>
              <a
                href={CHROME_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="store-pill"
                onClick={() => trackAppStoreClick('Chrome', 'hero')}
              >
                <span className="store-pill-ico"><Image src="/icons/chrome.svg" alt="" width={22} height={22} /></span>
                <span className="big">Chrome</span>
              </a>
            </div>
          </div>

          <div className="hero-viz-wrap">
            <ClientOnly fallback={heroVizPlaceholder}>
              <HeroViz variant="chaos" speed={1.5} />
            </ClientOnly>
          </div>
        </div>

        <div className="wrap atmos-content">
          <div className="hero-meta">
            <div><div className="k">hours saved per week</div><div className="v"><em>12+</em></div></div>
            <div><div className="k">apps replaced</div><div className="v"><em>10+</em></div></div>
            <div><div className="k">faster than typing</div><div className="v"><em>5&times;</em></div></div>
            <div><div className="k">active users</div><div className="v"><em>50K+</em></div></div>
          </div>
        </div>
      </section>

      {/* Personas */}
      <section className="section atmos-light">
        <div className="wrap atmos-content">
          <div className="section-head">
            <div className="eyebrow">Who it&rsquo;s for</div>
            <h2 className="section-h">Made for Professionals, Students, and <em>Entrepreneurs.</em></h2>
            <p className="section-sub">AVA adapts to your lifestyle &mdash; no matter how you work or study.</p>
          </div>
          <Personas />
        </div>
      </section>

      {/* Integrations band — works with everything you already use */}
      <section className="integrations-band">
        <div className="wrap atmos-content" style={{ textAlign: 'center', marginBottom: 14 }}>
          <div className="key">Works with everything you already use</div>
        </div>
        <Integrations />
      </section>

      {/* Cross-platform devices */}
      <section className="section atmos-light" id="devices">
        <div className="wrap atmos-content">
          <div className="section-head">
            <div className="eyebrow">Cross-platform</div>
            <h2 className="section-h">One App. <em>All Your Devices.</em></h2>
            <p className="section-sub">
              Use AVA anywhere you work &mdash; your plan stays perfectly in sync.
            </p>
          </div>
          <div className="devices-hero">
            <img
              src="/landing-v2/devices-hero.png"
              alt="AVA on iMac, MacBook Pro, iPad, iPhone and Apple Watch"
            />
          </div>
          <div className="device-chips">
            <span className="chip">iOS</span>
            <span className="chip">Android</span>
            <span className="chip">Web</span>
            <span className="chip">Chrome</span>
            <span className="chip chip-soon">Mac &middot; soon</span>
            <span className="chip chip-soon">iPad &middot; soon</span>
            <span className="chip chip-soon">Wearables &middot; soon</span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section atmos-light" id="features">
        <div className="wrap atmos-content">
          <div className="section-head">
            <div className="eyebrow">Every tool in one app</div>
            <h2 className="section-h">One AI Super App. <em>Everything You Need.</em></h2>
            <p className="section-sub">
              From basic tasks to advanced AI automation &mdash; AVA has everything you need to stay productive.
            </p>
          </div>
          <ClientOnly>
            <FeaturesGrid />
          </ClientOnly>
        </div>
      </section>

      {/* How it works — dark slab */}
      <section className="section atmos-dark" id="how">
        <div className="wrap atmos-content">
          <div className="section-head">
            <div className="eyebrow">How it works</div>
            <h2 className="section-h">Just Speak. <em>AVA Does the Rest.</em></h2>
            <p className="section-sub">No complex setup. No learning curve. Just natural conversation.</p>
          </div>
          <HowItWorks />
        </div>
      </section>

      {/* Testimonials */}
      <section className="section atmos-light" id="testimonials" style={{ paddingBottom: 80 }}>
        <div className="wrap atmos-content">
          <div className="section-head">
            <div className="eyebrow">Testimonials</div>
            <h2 className="section-h">Loved by <em>50,000+</em> Professionals.</h2>
            <p className="section-sub">
              Join thousands who are saving time and achieving more with AVA. <strong>4.7/5 App Store Rating.</strong>
            </p>
          </div>
        </div>
        <TestimonialsMarquee />
      </section>

      {/* Pricing */}
      <section className="section atmos-light" id="pricing">
        <div className="wrap atmos-content">
          <div className="section-head">
            <div className="eyebrow">Pricing</div>
            <h2 className="section-h">Start Free. <em>Upgrade Anytime.</em></h2>
            <p className="section-sub">Try Pro features free for 14 days. No credit card required.</p>
          </div>
          <Pricing platformLink={platformLink} isMobile={isMobile} />
        </div>
      </section>

      {/* FAQ */}
      <section className="section atmos-light" id="faq">
        <div className="wrap atmos-content">
          <div className="section-head">
            <div className="eyebrow">FAQ</div>
            <h2 className="section-h">Questions, <em>answered.</em></h2>
          </div>
          <FAQ />
        </div>
      </section>

      {/* Final CTA — dark slab */}
      <section className="final-cta">
        <div className="final-inner">
          <div className="pill"><span className="dot" />Ready when you are</div>
          <h2>Join <em>50,000+</em> people planning their day with AVA.</h2>
          <p>No credit card required &middot; 14-day free trial &middot; Cancel anytime.</p>
          <div className="hero-ctas">
            <a
              href={platformLink}
              target={target}
              rel={rel}
              className="btn btn-primary btn-lg"
              onClick={() => {
                trackCTAClick('Try AVA Free', 'final_cta')
                trackTrialStart('final_cta')
              }}
            >
              Try AVA Free <Icon name="arrow" size={16} stroke={2} />
            </a>
            <button type="button" onClick={openDemo} className="btn btn-secondary btn-lg">
              <Icon name="play" size={14} stroke={2} /> Watch AVA in action (30s)
            </button>
          </div>
          <div className="micro">iOS &middot; Android &middot; Web &middot; Chrome &mdash; synced automatically</div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="wrap">
          <div className="footer-grid">
            <div className="footer-brand">
              <a href="/" className="logo" style={{ color: 'var(--ink)' }}>
                <span className="logo-mark" />
                <span>AVA</span>
              </a>
              <p className="tag">
                Voice-first AI that <em>replaces 10+ apps</em> &mdash; tasks, calendar, notes,
                meetings, and video summaries &mdash; in one place.
              </p>
            </div>
            <div>
              <h4>Product</h4>
              <a href="#features" onClick={() => trackFooterLink('Features', 'product')}>Features</a>
              <a href="#pricing" onClick={() => trackFooterLink('Pricing', 'product')}>Pricing</a>
              <a href="/tutorials" onClick={() => trackFooterLink('Tutorials', 'product')}>Tutorials</a>
              <a href="/docs" onClick={() => trackFooterLink('Docs', 'product')}>Docs</a>
            </div>
            <div>
              <h4>Company</h4>
              <a href="/contact" onClick={() => trackFooterLink('Contact', 'company')}>Contact</a>
              <a href="mailto:info@agentava.ai" onClick={() => trackFooterLink('Help', 'company')}>Help</a>
              <a href="/privacy" onClick={() => trackFooterLink('Privacy', 'company')}>Privacy</a>
              <a href="/terms" onClick={() => trackFooterLink('Terms', 'company')}>Terms</a>
            </div>
            <div>
              <h4>Get the app</h4>
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackAppStoreClick('iOS', 'footer')}
              >iOS &mdash; App Store</a>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackAppStoreClick('Android', 'footer')}
              >Android &mdash; Play Store</a>
              <a href="/" onClick={() => trackFooterLink('Web app', 'app')}>Web app</a>
              <a
                href={CHROME_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackAppStoreClick('Chrome', 'footer')}
              >Chrome extension</a>
            </div>
          </div>
          <div className="footer-bottom">
            <span>&copy; 2026 AVA &middot; All rights reserved</span>
            <span>Made for people who think out loud</span>
          </div>
        </div>
      </footer>

      <VideoModal
        isOpen={videoOpen}
        onClose={() => setVideoOpen(false)}
        videoUrl={DEMO_VIDEO_URL}
      />
    </div>
  )
}
